/**
 * Lab-local time formatting.
 *
 * Every clinical timestamp in this app should render in the timezone of the
 * lab the user is currently scoped to (set in the header lab picker → stored
 * in `localStorage.selectedLabId`). Falls back to browser-local if no lab is
 * selected (e.g. a global admin viewing system-wide data).
 *
 * Date-only fields like DOB or recurrence_end_date must NOT be passed
 * through here — they have no time component and shifting them between zones
 * lands them on the wrong calendar day. Use a YYYY-MM-DD string-parser for
 * those (PatientsPage.formatDate).
 */
import api from '../services/api';

let cachedLabId: string | null = null;
let cachedLabTz: string | null = null;
let inflight: Promise<string | null> | null = null;
const localStorageKey = (id: string) => `labTz_${id}`;

/** Default fallback timezone when no lab is selected and we have no other
 * signal. We default to America/Los_Angeles because the platform is operated
 * out of California — better than UTC, which leaves users misreading clock
 * times by 7-8 hours when the lab cache hasn't warmed yet. Override via
 * `localStorage.defaultTimezone` for non-Pacific deployments. */
function browserTz(): string {
  try {
    const override = (typeof localStorage !== 'undefined' && localStorage.getItem('defaultTimezone')) || '';
    if (override) return override;
  } catch { /* ignore */ }
  return 'America/Los_Angeles';
}

/** Synchronously read the cached timezone for the currently-selected lab.
 * Triggers an async fetch to populate the cache the first time we see a new
 * lab id. Returns the browser zone until the cache is warm. */
export function getCurrentLabTimezone(): string {
  const labId = (typeof window !== 'undefined')
    ? (localStorage.getItem('selectedLabId') || '')
    : '';
  if (!labId) return browserTz();
  if (labId === cachedLabId && cachedLabTz) return cachedLabTz;
  // Try the per-lab localStorage cache first so we don't fetch on every page nav.
  try {
    const stored = localStorage.getItem(localStorageKey(labId));
    if (stored) {
      cachedLabId = labId;
      cachedLabTz = stored;
      return stored;
    }
  } catch { /* ignore */ }
  // Otherwise, kick off a background fetch and return the browser zone for now.
  if (!inflight) {
    inflight = api.get(`/api/labs/${labId}/timezone`)
      .then(r => {
        const tz = (r?.data?.timezone as string) || null;
        if (tz) {
          cachedLabId = labId;
          cachedLabTz = tz;
          try { localStorage.setItem(localStorageKey(labId), tz); } catch { /* ignore */ }
        }
        return tz;
      })
      .catch(() => null)
      .finally(() => { inflight = null; });
  }
  return browserTz();
}

/** Force-refresh the cached timezone — call this after the user picks a
 * different lab in the header so the next render uses the new zone. */
export function clearLabTimezoneCache(): void {
  cachedLabId = null;
  cachedLabTz = null;
}

/** Eagerly fetch and cache the current lab's timezone. Call this once on
 * app boot so the very first render of any timestamp uses the right zone
 * instead of the browser fallback. Returns the resolved IANA tz, or null. */
export async function prefetchLabTimezone(): Promise<string | null> {
  const labId = (typeof window !== 'undefined') ? (localStorage.getItem('selectedLabId') || '') : '';
  if (!labId || labId === '__all__') return null;
  try {
    const r = await api.get(`/api/labs/${labId}/timezone`);
    const tz = (r?.data?.timezone as string) || null;
    if (tz) {
      cachedLabId = labId;
      cachedLabTz = tz;
      try { localStorage.setItem(localStorageKey(labId), tz); } catch { /* ignore */ }
    }
    return tz;
  } catch {
    return null;
  }
}

type Kind = 'date' | 'time' | 'datetime' | 'datetime-short';

/* EVERY CLOCK IN THIS APP IS 24-HOUR.
 *
 * This is a clinical record. "8:15" on a timed trough, a stability window or
 * a chain-of-custody line has to mean one moment and not two, and AM/PM is
 * exactly the part that gets dropped when a time is read aloud, written on a
 * tube by hand, or re-typed from a screenshot. Laboratory medicine has used
 * a 24-hour clock for that reason for as long as it has kept records.
 *
 * `hourCycle: 'h23'` rather than `hourCycle: 'h23'`, because the latter renders
 * midnight as 24:00 in en-US — 24:10 on the 3rd is really 00:10 on the 4th,
 * so the one time of day that is already easiest to get wrong by a whole day
 * would print wrong too. */
const CLOCK: Intl.DateTimeFormatOptions = {
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
};

const STYLES: Record<Kind, Intl.DateTimeFormatOptions> = {
  date:              { year: 'numeric', month: '2-digit', day: '2-digit' },
  time:              { ...CLOCK },
  datetime:          { year: 'numeric', month: '2-digit', day: '2-digit', ...CLOCK },
  'datetime-short':  { month: 'numeric', day: 'numeric', ...CLOCK },
};

/** Core formatter — accepts anything new Date() can parse, plus YYYY-MM-DD
 * date-only strings (which are kept zone-free).
 *
 * Calling with NO ARGUMENTS defaults to "now" — this preserves the semantics
 * of the legacy `new Date().toLocaleString()` pattern that was bulk-migrated
 * to this helper. Calling with explicit null/undefined renders an empty string
 * (typical "data not yet present" case). */
export function formatInLabTz(value?: string | number | Date | null,
                               kind: Kind = 'datetime'): string {
  // Distinguish "no args passed" from "explicit undefined". A bare call like
  // formatLabDateTime() means "current time"; an explicit undefined (typically
  // from a possibly-empty field) means "render as blank".
  // eslint-disable-next-line prefer-rest-params
  if (arguments.length === 0) value = new Date();
  if (value === null || value === undefined || value === '') return '';
  // Date-only string: render as-is, zone-free.
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (m) {
      if (kind === 'time' || kind === 'datetime-short') return '';
      return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
    }
  }
  // A date-TIME string with no zone marker is UTC — that is what the API
  // emits (2026-08-02T17:50:37.701762, no Z, no offset). `new Date()` reads an
  // unmarked date-time as LOCAL, so the value was being shifted once on parse
  // and again on formatting into the lab zone: a claim submitted at 17:50 UTC
  // rendered as 03:50 Pacific instead of 10:50. Marking it explicitly is the
  // one place that fixes every timestamp in the app at once.
  const d = value instanceof Date
    ? value
    : (typeof value === 'string' && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(value)
        ? new Date(value + 'Z')
        : new Date(value));
  if (Number.isNaN(d.getTime())) return String(value);
  const tz = getCurrentLabTimezone();
  try {
    return new Intl.DateTimeFormat('en-US', { ...STYLES[kind], timeZone: tz }).format(d);
  } catch {
    return d.toLocaleString('en-US', STYLES[kind]);
  }
}

/** Format a SCHEDULED wall-clock time — "09:00", "09:00:00", "2026-08-25T09:00".
 *
 * Not every time in the system is an instant. `appointments.scheduled_start_time`
 * is a time of day: 09:00 means nine in the morning at the patient's kitchen
 * table, in the lab's own zone. It is not a UTC timestamp and it must not be
 * converted.
 *
 * formatInLabTz above deliberately reads an unmarked date-time as UTC, which is
 * right for the naive-UTC instants the API emits and wrong for these. Running a
 * wall-clock 09:00 through it produced 02:00 Pacific — the tech app listed a
 * 9 AM draw as 2 AM and a 9 PM draw as 2 PM while Dispatch, which prints the
 * raw string, showed both correctly. Same appointment, two different times, on
 * two screens the same person uses in one shift.
 *
 * Use this for anything a human SCHEDULED. Use formatLabTime for anything that
 * HAPPENED (created_at, collected_at, resulted_at). */
export function formatLabWallTime(value?: string | null): string {
  if (!value) return '';
  const raw = String(value);
  // Accept a bare time, or take the time half of a date-time.
  const m = /(\d{1,2}):(\d{2})/.exec(raw.includes('T') ? raw.split('T')[1] || '' : raw);
  if (!m) return '';
  const h = parseInt(m[1], 10);
  const min = m[2];
  if (Number.isNaN(h)) return '';
  // 24-hour, zero-padded, for the same reason as CLOCK above: a scheduled
  // "7:30" that could be a morning draw or an evening one is not a schedule.
  return `${String(h % 24).padStart(2, '0')}:${min}`;
}

export const formatLabDate     = (v: any) => formatInLabTz(v, 'date');
export const formatLabTime     = (v: any) => formatInLabTz(v, 'time');
export const formatLabDateTime = (v: any) => formatInLabTz(v, 'datetime');
export const formatLabDateTimeShort = (v: any) => formatInLabTz(v, 'datetime-short');

/** "Today" as YYYY-MM-DD in the currently-scoped lab's timezone.
 *
 * REPLACES the widespread `new Date().toISOString().slice(0, 10)` pattern,
 * which is always UTC and silently rolls to tomorrow after ~5pm PDT (Pacific)
 * — meaning date pickers, "today" filters, and same-day form fields default
 * to a day that hasn't started in the user's zone. Use this helper for any
 * default-to-today value that a lab user reads or writes.
 *
 * Falls back to the browser default (browserTz → America/Los_Angeles) until
 * the lab-timezone cache is warm; the returned date only changes by ±1 day
 * across timezones, which is the correct answer either way. */
export function todayInLabTz(): string {
  const tz = getCurrentLabTimezone();
  try {
    // en-CA emits ISO-style YYYY-MM-DD natively.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch {
    // Browser doesn't support the tz; fall back to browser-local (still
    // strictly better than UTC).
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}

/**
 * The calendar day a timestamp falls on IN THE LAB'S ZONE, as YYYY-MM-DD.
 *
 * For comparing against an <input type="date">, which is already YYYY-MM-DD.
 * Comparing those as STRINGS is the whole point: it is the one comparison
 * with no zone in it, so a filter cannot disagree with the column beside it.
 *
 * The claims date filter did it with raw Date objects and got three different
 * zones into one comparison:
 *   new Date('2026-10-07')            → UTC midnight     (date-only ISO is UTC)
 *   new Date('2026-10-07T23:59:59')   → LOCAL midnight   (no marker = local)
 *   new Date('2026-10-08 00:57:14')   → LOCAL            (but it is UTC in the
 *                                                         database)
 * So the lower bound was seven hours early, the upper bound was not, and a
 * claim submitted at 17:57 Pacific on the 7th — which the Submitted column
 * correctly showed as 10/07 — was being tested as if it were the 8th.
 */
export function labDateKey(value?: string | number | Date | null): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    // A date-only value has no zone to convert; it is already the answer.
    if (m && value.length === 10) return value;
  }
  try {
    const tz = getCurrentLabTimezone();
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(
      // Same naive-UTC rule as formatInLabTz: an unmarked date-time is UTC,
      // which is what the API emits.
      value instanceof Date ? value
        : (typeof value === 'string' && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(value)
            ? new Date(value + 'Z')
            : new Date(value)));
  } catch {
    return '';
  }
}

/** Returns a short label like "PT" / "ET" derived from the lab's IANA zone. */
export function labTimezoneAbbr(): string {
  const tz = getCurrentLabTimezone();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, timeZoneName: 'short',
    }).formatToParts(new Date());
    const tzn = parts.find(p => p.type === 'timeZoneName');
    return tzn ? tzn.value : '';
  } catch {
    return '';
  }
}


/** Calendar days a claim has been outstanding, counted in the lab's timezone.
 *
 * AR aging is counted in calendar days, not 24-hour blocks. A claim submitted
 * at 6pm yesterday is one day out this morning — flooring elapsed hours called
 * it zero, which understates every claim submitted in the evening and quietly
 * delays the point at which one looks overdue.
 *
 * Both sides are reduced to a date in the lab's zone before subtracting, so a
 * claim does not gain or lose a day because the office is west of UTC.
 */
export function daysOutInLabTz(value?: string | number | Date | null): number | null {
  if (value === null || value === undefined || value === '') return null;
  const s = String(value);
  const d = value instanceof Date
    ? value
    : (/^\d{4}-\d{2}-\d{2}$/.test(s)
        ? new Date(s + 'T12:00:00Z')                    // date-only: midday, zone-free
        : new Date(/[Zz]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + 'Z'));
  if (Number.isNaN(d.getTime())) return null;
  const tz = getCurrentLabTimezone();
  const dayIn = (x: Date) => {
    const p = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(x);
    return Date.UTC(+p.slice(0, 4), +p.slice(5, 7) - 1, +p.slice(8, 10));
  };
  return Math.max(0, Math.round((dayIn(new Date()) - dayIn(d)) / 86400000));
}

/** "Now" formatted for an `<input type="datetime-local">` — LOCAL wall clock.
 *
 * REPLACES `new Date().toISOString().slice(0, 16)`, which was used in nine
 * places and is wrong every time: toISOString() is UTC, and a datetime-local
 * input renders whatever string it is given as literal wall-clock. West of
 * UTC the field therefore showed a time in the FUTURE — a phlebotomist
 * collecting at 9:24 PM on Aug 26 in Los Angeles pressed "Now" and the field
 * read 08/27/2026, 4:24 AM. Tomorrow's date, on a specimen drawn today.
 *
 * It compounds on submit: `new Date(field).toISOString()` parses an unmarked
 * string as LOCAL, so the already-shifted value is shifted again — 14 hours
 * off the real draw time, which is enough to matter for a timed trough or a
 * stability window.
 *
 * Uses the LAB's timezone, not the browser's, so a dispatcher in another
 * state stamping a collection records the lab's wall clock.
 */
export function nowForDateTimeLocalInput(when?: Date): string {
  const d = when instanceof Date ? when : new Date();
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: getCurrentLabTimezone(),
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d).reduce((a: any, x) => (a[x.type] = x.value, a), {});
  // en-CA gives 24-hour parts; hour can come back as "24" at midnight.
  const hh = p.hour === '24' ? '00' : p.hour;
  return `${p.year}-${p.month}-${p.day}T${hh}:${p.minute}`;
}
